import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import styled from 'styled-components';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useQuery } from '@tanstack/react-query';
import { fetchNodeConnections, fetchNodeDetails, fetchCompleteNodeData } from '../../services/api';
import { NodePosition, NodeConnection, NodeDetails, Packet } from '../../types/api';
import Spinner from '../ui/Spinner';
import ErrorMessage from '../ui/ErrorMessage';
import { Feature, FeatureCollection, LineString, GeoJsonProperties } from 'geojson';

// Color utilities for node age visualization
const interpolateColor = (startColor: [number, number, number], endColor: [number, number, number], progress: number): [number, number, number] => {
  return [
    startColor[0] + (endColor[0] - startColor[0]) * progress,
    startColor[1] + (endColor[1] - startColor[1]) * progress,
    startColor[2] + (endColor[2] - startColor[2]) * progress
  ];
};

const hslToHex = (h: number, s: number, l: number): string => {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

const getNodeAgeColor = (lastHeardMinutes: number | null): string => {
  // Default gray for no data or invalid values
  if (lastHeardMinutes === null || !isFinite(lastHeardMinutes)) return '#95a5a6';
  
  // Clamp minutes to 0-60 range and calculate progress
  const minutes = Math.min(Math.max(lastHeardMinutes, 0), 60);
  const progress = minutes / 60;
  
  // Define color stops for our gradient
  type ColorStop = { h: number; s: number; l: number };
  type ColorStops = { [key: string]: ColorStop };
  
  const colors: ColorStops = {
    '0': { h: 140, s: 76, l: 35 },    // Rich forest green (#1B7A3D)
    '0.25': { h: 150, s: 65, l: 40 }, // Blue-tinted forest (#207A59)
    '0.5': { h: 200, s: 55, l: 45 },  // Ocean blue (#3B7B9E)
    '0.75': { h: 210, s: 25, l: 50 }, // Faded blue-grey (#6F8A99)
    '1': { h: 220, s: 10, l: 55 }     // Neutral grey (#868E96)
  };
  
  const stops = [0, 0.25, 0.5, 0.75, 1];
  
  // Find the two colors to interpolate between
  let lower = stops[0];
  let upper = stops[stops.length - 1];
  
  // Find proper color stops
  for (let i = 0; i < stops.length - 1; i++) {
    if (progress >= stops[i] && progress <= stops[i + 1]) {
      lower = stops[i];
      upper = stops[i + 1];
      break;
    }
  }
  
  // Safety check - if we somehow got invalid stops, return the neutral grey
  if (!colors[lower.toString()] || !colors[upper.toString()]) {
    return '#95a5a6';
  }
  
  // Calculate how far we are between the two stops
  const stopProgress = (progress - lower) / (upper - lower);
  
  // Interpolate between the two colors
  const lowerColor = colors[lower.toString()];
  const upperColor = colors[upper.toString()];
  
  const h = lowerColor.h + (upperColor.h - lowerColor.h) * stopProgress;
  const s = lowerColor.s + (upperColor.s - lowerColor.s) * stopProgress;
  const l = lowerColor.l + (upperColor.l - lowerColor.l) * stopProgress;
  
  try {
    return hslToHex(h, s / 100, l / 100);
  } catch (error) {
    console.warn('Color interpolation failed:', { h, s, l, progress, minutes });
    return '#95a5a6'; // Fallback to neutral grey on any error
  }
};

// Set Mapbox access token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || 'pk.eyJ1IjoidHdwbWFwcyIsImEiOiJjbHg0dDFsajkxYTU2MnFxNnJsZng3bDYwIn0.IuNRYkpy8qoMcxzSQ47Mcg';

// Disable Mapbox telemetry
// @ts-ignore - SEND_EVENTS exists but is not in TypeScript definitions
if (mapboxgl.config && typeof mapboxgl.config.SEND_EVENTS !== 'undefined') {
  try {
    // @ts-ignore
    mapboxgl.config.SEND_EVENTS = false;
  } catch (e) {
    console.warn('Could not disable Mapbox telemetry events:', e);
  }
}

// Disable error reporting to Mapbox servers
// @ts-ignore
if (mapboxgl.errorReporter) {
  try {
    // @ts-ignore
    mapboxgl.errorReporter.disable();
  } catch (e) {
    console.warn('Could not disable Mapbox error reporter:', e);
  }
}

/**
 * Props for the MapControls component
 */
interface MapControlsProps {
  showDebug: boolean;
  setShowDebug: (show: boolean) => void;
  connectionTypes: string[];
  collapsed: boolean;
  onToggleCollapse: () => void;
}

interface StyledHeaderProps {
  $collapsed: boolean;
}

/**
 * MapControls component for legend and debug toggle
 */
const MapControls: React.FC<MapControlsProps> = ({ 
  showDebug, 
  setShowDebug, 
  connectionTypes = [], 
  collapsed = true, 
  onToggleCollapse 
}) => {
  // Add a debug toggle function with logging
  const handleDebugToggle = () => {
    const newDebugState = !showDebug;
    console.log(`Debug toggle clicked. Current state: ${showDebug}, new state: ${newDebugState}`);
    setShowDebug(newDebugState);
  };
  
  return (
    <ControlsPanel $collapsed={collapsed}>
      <ControlsHeader $collapsed={collapsed} onClick={onToggleCollapse}>
        <HeaderText>Map Legend</HeaderText>
        <CollapseArrow>{collapsed ? '▼' : '▲'}</CollapseArrow>
      </ControlsHeader>
      
      {!collapsed && (
        <LegendContent>
          <LegendSection>
            <LegendTitle>Nodes</LegendTitle>
            <LegendItem>
              <ColorScale>
                {[0, 15, 30, 45, 60].map((minutes) => (
                  <ColorStep key={minutes} color={getNodeAgeColor(minutes)}>
                    <ColorLabel>{minutes}m</ColorLabel>
                  </ColorStep>
                ))}
              </ColorScale>
              <LegendText>Time Since Last Heard</LegendText>
            </LegendItem>
            
            {/* Add recently heard node color scale */}
            <LegendItem>
              <RecentlyHeardScale>
                {[0, 1, 2, 3, 4].map((index) => {
                  // Create a gradient from purple (#9b59b6) to green (#2ecc71)
                  const purple = [155, 89, 182]; // #9b59b6 in RGB
                  const green = [46, 204, 113];  // #2ecc71 in RGB
                  const progress = index / 4; // 4 is the max index (0-4 for 5 nodes)
                  const interpolated = [
                    Math.round(purple[0] + (green[0] - purple[0]) * progress),
                    Math.round(purple[1] + (green[1] - purple[1]) * progress),
                    Math.round(purple[2] + (green[2] - purple[2]) * progress)
                  ];
                  const color = `rgb(${interpolated[0]}, ${interpolated[1]}, ${interpolated[2]})`;
                  return (
                    <RecentlyHeardStep key={index} color={color}>
                      <RecentlyHeardLabel>{index + 1}</RecentlyHeardLabel>
                    </RecentlyHeardStep>
                  );
                })}
              </RecentlyHeardScale>
              <LegendText>Recently Heard (1-5)</LegendText>
            </LegendItem>
            
            <BasicLegendItems>
              <LegendItem>
                <NodeDot style={{ backgroundColor: '#3498db', border: '2px solid #fff' }} />
                <LegendText>Gateway Node</LegendText>
              </LegendItem>
              <LegendItem>
                <EmojiNode>📡</EmojiNode>
                <LegendText>HALL Node</LegendText>
              </LegendItem>
              <LegendItem>
                <EmojiNode>🏠</EmojiNode>
                <LegendText>Emoji Nodes</LegendText>
              </LegendItem>
            </BasicLegendItems>
          </LegendSection>
          
          {connectionTypes.length > 0 && (
            <LegendSection>
              <LegendTitle>Connections</LegendTitle>
              <ConnectionsGrid>
                {connectionTypes.includes('direct_good') && (
                  <LegendItem>
                    <ConnectionLine style={{ backgroundColor: '#2ecc71' }} />
                    <LegendText>Direct (Good)</LegendText>
                  </LegendItem>
                )}
                {connectionTypes.includes('direct_medium') && (
                  <LegendItem>
                    <ConnectionLine style={{ backgroundColor: '#27ae60' }} />
                    <LegendText>Direct (Medium)</LegendText>
                  </LegendItem>
                )}
                {connectionTypes.includes('direct_poor') && (
                  <LegendItem>
                    <ConnectionLine style={{ backgroundColor: '#1e8449' }} />
                    <LegendText>Direct (Poor)</LegendText>
                  </LegendItem>
                )}
                {connectionTypes.includes('multi_hop') && (
                  <LegendItem>
                    <ConnectionLine style={{ backgroundColor: '#9c27b0', borderStyle: 'dashed' }} />
                    <LegendText>Multi-hop</LegendText>
                  </LegendItem>
                )}
                {connectionTypes.includes('inferred') && (
                  <LegendItem>
                    <ConnectionLine style={{ backgroundColor: '#ff9800', borderStyle: 'dotted' }} />
                    <LegendText>Inferred</LegendText>
                  </LegendItem>
                )}
                {connectionTypes.includes('historical') && (
                  <LegendItem>
                    <ConnectionLine style={{ backgroundColor: '#607d8b', opacity: 0.5, borderStyle: 'dashed' }} />
                    <LegendText>Historical</LegendText>
                  </LegendItem>
                )}
              </ConnectionsGrid>
            </LegendSection>
          )}
          
          <DebugToggle>
            <DebugButton onClick={handleDebugToggle}>
              {showDebug ? 'Hide Debug Info' : 'Show Debug Info'}
            </DebugButton>
          </DebugToggle>
        </LegendContent>
      )}
    </ControlsPanel>
  );
};

// Export the MapControls component for use in other files
export { MapControls };

/**
 * Props for the ModernView component
 */
interface ModernViewProps {
  onNodeSelect: (nodeId: number | undefined) => void;
  selectedNodeId?: number;
  zoom?: number;
  showOfflineNodes?: boolean;
  autoCenter?: boolean;
  showDebug?: boolean;
  onShowDebugChange?: (show: boolean) => void;
}

// Add an emoji detection helper function after the imports
const containsEmoji = (str: string): boolean => {
  // This regex pattern matches most common emoji ranges
  const emojiRegex = /[\u{1F300}-\u{1F6FF}\u{1F900}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u;
  return emojiRegex.test(str);
};

// Create a function for creating emoji marker elements - Drastically Simplified
const createEmojiMarker = (emoji: string, size: number = 28): HTMLElement => {
  const el = document.createElement('div');
  // No specific class needed for now, Mapbox adds its own
  el.innerHTML = emoji;
  el.style.fontSize = `${size}px`;
  el.style.lineHeight = '1'; // Ensure consistent height
  el.style.textAlign = 'center';
  el.style.cursor = 'pointer';
  // Basic styles to prevent interference
  el.style.background = 'none';
  el.style.border = 'none';
  el.style.padding = '0';
  el.style.margin = '0';
  el.style.boxShadow = 'none';
  el.style.width = 'auto'; // Let content determine width initially
  el.style.height = 'auto';
  return el;
};

/**
 * ModernView component - optimized version of MapView
 */
const ModernView = forwardRef<{ 
  autoCenter: () => void; 
  handleNewPacket: (packet: Packet) => void; 
  isScrolling: boolean; 
  queuePacketForHighlight: (packet: Packet) => void; 
  activeConnectionTypes: string[] 
}, ModernViewProps>(({
  onNodeSelect, 
  selectedNodeId,
  zoom = 8,
  showOfflineNodes = true,
  autoCenter = true,
  showDebug = false,
  onShowDebugChange
}, ref) => {
  // State and refs
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<Record<string, mapboxgl.Marker>>({});
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const packetPopupRef = useRef<mapboxgl.Popup | null>(null);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [legendCollapsed, setLegendCollapsed] = useState<boolean>(true);
  const [highlightedNodeId, setHighlightedNodeId] = useState<number | null>(null);
  const [latestPacket, setLatestPacket] = useState<Packet | null>(null);
  const [activeConnectionTypes, setActiveConnectionTypes] = useState<string[]>([]);
  const [lastHeardNodes, setLastHeardNodes] = useState<number[]>([]);
  
  // Use ref for scrolling state to avoid re-renders
  const isScrollingRef = useRef(false);
  const packetQueue = useRef<Packet[]>([]);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastHeardCleanupRef = useRef<NodeJS.Timeout | null>(null);
  const packetPopupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Use this to track which elements have click handlers to avoid duplicates
  const clickHandlersMap = useRef(new WeakMap<HTMLElement, boolean>());
  
  // Keep track of packet popups for all recently heard nodes
  const packetPopupsRef = useRef<Record<string, mapboxgl.Popup>>({});
  
  // Data queries
  const { 
    data: nodePositions, 
    isLoading: isLoadingPositions, 
    error: positionsError,
    refetch: refetchPositions
  } = useQuery({
    queryKey: ['completeNodeData'],
    queryFn: fetchCompleteNodeData,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const { 
    data: nodeConnections, 
    isLoading: isLoadingConnections, 
    error: connectionsError,
    refetch: refetchConnections
  } = useQuery({
    queryKey: ['nodeConnections'],
    queryFn: fetchNodeConnections,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Initialize map once
  useEffect(() => {
    if (!mapContainer.current || mapInitialized) return;
    
    try {
      setMapInitialized(true);
      console.log('Initializing modern map view...');
      
      // Create map with offline-friendly options and better rendering settings
      const newMap = new mapboxgl.Map({
        container: mapContainer.current,
        style: import.meta.env.VITE_MAP_STYLE || 'mapbox://styles/mapbox/dark-v11',
        center: [-71.0589, 42.3601], // Boston
        zoom: zoom,
        attributionControl: true,
        trackResize: true,
        renderWorldCopies: false,         // Prevents map from rendering multiple copies of world
        preserveDrawingBuffer: true,      // Important for some browsers
        antialias: true,                  // Better rendering
        collectResourceTiming: false,     // Disable performance monitoring
        refreshExpiredTiles: false,       // Reduce network requests
        fadeDuration: 0,                  // Reduce animation for better performance
        localIdeographFontFamily: "'Noto Sans', 'Noto Sans CJK SC', sans-serif"
      });
      
      // Add basic error handling for map
      newMap.on('error', (e) => {
        console.error('Mapbox error:', e);
      });
      
      // Add a custom event listener to track when map operations are complete
      let mapOperationsInProgress = 0;
      
      newMap.on('dataloading', () => {
        mapOperationsInProgress++;
      });
      
      newMap.on('dataabort', () => {
        if (mapOperationsInProgress > 0) mapOperationsInProgress--;
      });
      
      newMap.on('data', () => {
        if (mapOperationsInProgress > 0) mapOperationsInProgress--;
      });
      
      // Add navigation controls
      newMap.addControl(new mapboxgl.NavigationControl());
      
      // Store map reference
      map.current = newMap;
      
      // Create a single, reusable popup instance
      popupRef.current = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: false,
        offset: [0, -50],
        className: 'mapboxgl-popup-main',
        maxWidth: '300px'
      });
      
      // Track when map is being interacted with
      newMap.on('movestart', () => {
        isScrollingRef.current = true;
      });
      
      // Handle map interactions completed
      newMap.on('moveend', () => {
        setTimeout(() => {
          isScrollingRef.current = false;
          if (packetQueue.current.length > 0) {
            const packet = packetQueue.current.shift();
            if (packet) handleNewPacket(packet);
          }
        }, 100);
      });
      
      // Set up map event handlers once loaded
      newMap.on('load', () => {
        console.log('Modern map loaded');
        setMapLoaded(true);
        
        // Add source for connections
        newMap.addSource('connections', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          }
        });
        
        // Add layer for connections
        newMap.addLayer({
          id: 'connections-layer',
          type: 'line',
          source: 'connections',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': [
              'case',
              ['==', ['get', 'connectionType'], 'multi_hop'], '#9c27b0',  // Purple for multi-hop
              ['==', ['get', 'connectionType'], 'inferred'], '#ff9800',   // Orange for inferred
              ['==', ['get', 'isHistorical'], true], '#607d8b',           // Gray for historical
              [
                'interpolate',
                ['linear'],
                ['get', 'quality'],
                0, '#1e8449',     // Dark green for poor quality
                50, '#27ae60',    // Medium green for medium quality
                100, '#2ecc71'    // Bright green for good quality
              ]
            ],
            'line-width': [
              'interpolate',
              ['linear'],
              ['zoom'],
              5, 1.5,
              10, 2.5,
              15, 3.5
            ],
            'line-dasharray': [
              'case',
              ['==', ['get', 'connectionType'], 'multi_hop'], ['literal', [2, 1]],  // Dash for multi-hop
              ['==', ['get', 'connectionType'], 'inferred'], ['literal', [1, 1]],   // Dot for inferred
              ['==', ['get', 'isHistorical'], true], ['literal', [3, 2]],           // Long dash for historical
              ['literal', [1, 0]]  // Solid for direct connections
            ],
            'line-opacity': [
              'case',
              ['==', ['get', 'isHistorical'], true], 0.5,  // Lower opacity for historical
              0.9  // Higher opacity for other connections
            ]
          }
        });
      });
      
      // Clean up on unmount
      return () => {
        // Clear any timeouts
        if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        if (lastHeardCleanupRef.current) clearTimeout(lastHeardCleanupRef.current);
        if (packetPopupTimeoutRef.current) clearTimeout(packetPopupTimeoutRef.current);
        
        // Need to remove click listeners to avoid memory leaks
        try {
          // Clear all click handlers from markers
          Object.values(markers.current).forEach(marker => {
            const el = marker.getElement();
            el.onclick = null;
            marker.remove();
          });
          
          // Close and clear any popups
          if (popupRef.current) popupRef.current.remove();
          if (packetPopupRef.current) packetPopupRef.current.remove();
          
          // Clear all packet popups
          Object.values(packetPopupsRef.current).forEach(popup => {
            popup.remove();
          });
          
          // Clear popup references
          popupRef.current = null;
          packetPopupRef.current = null;
          packetPopupsRef.current = {};
        } catch (e) {
          console.error('Error cleaning up markers/popups:', e);
        }
        
        // Finally remove the map
        if (map.current) {
          map.current.remove();
          map.current = null;
        }
        
        markers.current = {};
      };
    } catch (error) {
      console.error('Error initializing modern map:', error);
      setMapLoaded(true); // Force loaded state to show error message
    }
  }, [zoom]);

  // --- Effect to manage the main node popup ---
  useEffect(() => {
    if (!map.current || !nodePositions) return;
    
    // If popup ref doesn't exist, create it just once
    if (!popupRef.current) {
      popupRef.current = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: false,
        offset: [0, -50],
        className: 'mapboxgl-popup-main', // Add a custom class for styling
        maxWidth: '300px'
      });
    }
    
    // Create content and show popup if a node is selected
    if (selectedNodeId !== undefined && map.current.loaded()) {
      const node = nodePositions.find(n => n.nodeId === selectedNodeId);
      const marker = markers.current[String(selectedNodeId)];
      
      if (node && marker) {
        // Generate HTML content
        const nodeName = node.details?.longName || `Node ${node.nodeId}`;
        const hexInfo = node.details?.nodeHex ? `<span style="color: #666;">(${node.details.nodeHex})</span>` : '';
        const batteryColor = node.batteryLevel ? 
          (node.batteryLevel < 20 ? '#e74c3c' : node.batteryLevel < 50 ? '#f39c12' : '#27ae60') : '';
        
        const popupHtml = `
          <div style="font-family: sans-serif;" class="popup-content">
            <div class="node-name"><strong>${nodeName}</strong>${hexInfo ? ' ' + hexInfo : ''}</div>
            <div class="node-status">
              <span style="color: ${node.isOnline ? '#27ae60' : '#e74c3c'};">
                Status: ${node.isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
            ${node.batteryLevel ? `<div class="node-battery">Battery: <span style="color: ${batteryColor};">${node.batteryLevel}%</span></div>` : ''}
            <div class="node-coords">
              <span style="color: #666;">Coordinates: ${isFinite(node.latitude) ? node.latitude.toFixed(5) : 'N/A'}, ${isFinite(node.longitude) ? node.longitude.toFixed(5) : 'N/A'}</span>
            </div>
          </div>
        `;
        
        // Show the popup with the content
        popupRef.current
          .setLngLat(marker.getLngLat())
          .setHTML(popupHtml)
          .addTo(map.current);
          
        // Set popup z-index again after adding to map to be sure
        setTimeout(() => {
          const popupElement = popupRef.current?.getElement();
          if (popupElement) {
            popupElement.style.zIndex = '3000';
          }
        }, 0);
      }
    } else {
      // Remove popup if no node is selected
      if (popupRef.current) popupRef.current.remove();
    }
  }, [selectedNodeId, nodePositions, markers, onNodeSelect]);

  // Update markers and connections when data changes
  useEffect(() => {
    if (!map.current || !mapLoaded || !nodePositions || !nodeConnections) return;
    
    console.log('Updating modern map data...');
    
    // Get the self node ID from environment variable
    const selfNodeId = import.meta.env.VITE_SELF_NODE_ID ? 
      parseInt(import.meta.env.VITE_SELF_NODE_ID as string, 10) : undefined;
    
    // Extract types of connections for legend
    const connectionTypeSet = new Set<string>();
    
    // Filter nodes based on showOfflineNodes
    const validNodes = nodePositions.filter(node => 
      node.latitude !== null && node.longitude !== null &&
      isFinite(node.longitude) && isFinite(node.latitude)
    );
    
    // Always include the self node, highlighted node, and last heard nodes when filtering
    const visibleNodes = validNodes.filter(node => 
      node.isOnline || 
      showOfflineNodes || 
      (selfNodeId && node.nodeId === selfNodeId) || 
      node.nodeId === highlightedNodeId || 
      lastHeardNodes.includes(node.nodeId)
    );
    
    console.log(`Total nodes: ${nodePositions.length}, Valid nodes: ${validNodes.length}, Visible nodes: ${visibleNodes.length}`);
    
    // Track existing and new marker IDs to determine which ones to add/remove
    const existingMarkerIds = new Set(Object.keys(markers.current));
    const newMarkerIds = new Set<string>();
    
    // Update marker color based on node age
    const getMarkerColor = (node: NodePosition): string => {
      // Always return blue for gateways
      if (node.isGateway) return '#3498db';
      
      // If node is not online and has no lastHeard, return grey
      if (!node.isOnline && !node.lastHeard) return '#95a5a6';
      
      // Check if node is in lastHeardNodes (recently heard)
      const heardIndex = lastHeardNodes.indexOf(node.nodeId);
      if (heardIndex !== -1 && heardIndex < 5) {
        // Create a gradient from purple (#9b59b6) to green (#2ecc71)
        // Most recent is purple (index 0), oldest is closest to green (index 4)
        const purple = [155, 89, 182]; // #9b59b6 in RGB
        const green = [46, 204, 113];  // #2ecc71 in RGB
        
        // Calculate progress (0 to 1) with 0 being most recent (purple)
        const progress = heardIndex / 4; // 4 is the max index (0-4 for 5 nodes)
        
        // Interpolate colors
        const interpolated = [
          Math.round(purple[0] + (green[0] - purple[0]) * progress),
          Math.round(purple[1] + (green[1] - purple[1]) * progress),
          Math.round(purple[2] + (green[2] - purple[2]) * progress)
        ];
        
        return `rgb(${interpolated[0]}, ${interpolated[1]}, ${interpolated[2]})`;
      }
      
      // For nodes not in the last 5 heard, use the regular age-based color
      const lastHeardMinutes = node.lastHeard 
        ? Math.floor((Date.now() - new Date(node.lastHeard).getTime()) / (1000 * 60))
        : node.isOnline ? 0 : 60; // If online but no lastHeard, assume just heard; if offline, assume 60 min
      
      return getNodeAgeColor(lastHeardMinutes);
    };

    // Update or create markers for each visible node
    visibleNodes.forEach(node => {
      const nodeIdStr = String(node.nodeId);
      newMarkerIds.add(nodeIdStr);
      
      const isSelected = node.nodeId === selectedNodeId;
      const isHighlighted = node.nodeId === highlightedNodeId;
      
      // Create popup content (we'll use this later for manual popup)
      const nodeName = node.details?.longName || `Node ${node.nodeId}`;
      const hexInfo = node.details?.nodeHex ? `<span style=\"color: #666;\">(${node.details.nodeHex})</span>` : '';
      
      // --- Determine marker options and type ---
      let isEmojiMarker = false;
      let emoji = '';
      let emojiSize = 28;
      let markerColor = getMarkerColor(node);
      let scale = 1.0;
      
      // Check if it's the self node FIRST
      if (selfNodeId !== undefined && node.nodeId === selfNodeId) {
        isEmojiMarker = true;
        emoji = '📡';
      } else if (node.details?.shortName) {
        // Existing emoji logic based on shortName
        const shortName = node.details.shortName;
        if (containsEmoji(shortName)) {
          isEmojiMarker = true;
          const match = shortName.match(/[\u{1F300}-\u{1F6FF}\u{1F900}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u);
          emoji = match ? match[0] : '📍';
        }
      }

      if (isHighlighted) {
        markerColor = '#9b59b6'; // Keep purple for highlighted nodes
        scale = 1.2;
        if (isEmojiMarker) emojiSize = 36;
      } else if (isSelected) {
        scale = 1.1;
        if (isEmojiMarker) emojiSize = 32;
      }
      // --- End marker options ---
      
      // --- Create or Update Marker ---
      let marker = markers.current[nodeIdStr];
      
      if (!marker) {
          // Create new marker - without any custom click handlers!
          if (isEmojiMarker) {
              const emojiEl = createEmojiMarker(emoji, emojiSize);
              marker = new mapboxgl.Marker({ 
                  element: emojiEl, 
                  anchor: 'center',
                  // Add clickTolerance to make it easier to click
                  clickTolerance: 10
              })
              .setLngLat([node.longitude, node.latitude])
              .addTo(map.current!);
          } else {
              marker = new mapboxgl.Marker({ 
                  color: markerColor, 
                  scale: scale,
                  // Add clickTolerance to make it easier to click
                  clickTolerance: 10
              })
              .setLngLat([node.longitude, node.latitude])
              .addTo(map.current!);
          }
          
          // Use Mapbox's built-in click handling
          marker.getElement().onclick = function(e) {
              // Prevent default browser behavior
              e.preventDefault();
              // Don't let the event propagate to other elements
              e.stopPropagation();
              // Call the node select function directly
              onNodeSelect(node.nodeId);
              // Return false to prevent default
              return false;
          };
          
          markers.current[nodeIdStr] = marker;
      } else {
          // Update existing marker
          marker.setLngLat([node.longitude, node.latitude]);
          const element = marker.getElement();
          
          // Always make sure the click handler is properly set
          element.onclick = function(e) {
              // Prevent default browser behavior
              e.preventDefault();
              // Don't let the event propagate to other elements
              e.stopPropagation();
              // Call the node select function directly
              onNodeSelect(node.nodeId);
              // Return false to prevent default
              return false;
          };
          
          if (isEmojiMarker) {
              // For emoji markers, we need to update the content and size
              // It might be simpler to remove and re-add if the type changes, but let's try updating
              if (element.innerHTML !== emoji) element.innerHTML = emoji;
              if (element.style.fontSize !== `${emojiSize}px`) element.style.fontSize = `${emojiSize}px`;
          } else {
              // Update standard marker color and scale
              const svgPath = element.querySelector('path');
              if (svgPath && svgPath.getAttribute('fill') !== markerColor) {
                  svgPath.setAttribute('fill', markerColor);
              }
              const currentScaleMatch = element.style.transform.match(/scale\(([^)]+)\)/);
              const currentScale = currentScaleMatch ? parseFloat(currentScaleMatch[1]) : 1.0;
              if (currentScale !== scale) {
                element.style.transform = `translate(-50%, -50%) scale(${scale})`;
              }
          }
          
          // --- Update zIndex based on highlighted/selected state --- 
          let zIndex = '50'; // Default
          if (isHighlighted) {
              zIndex = '2000'; 
          } else if (isSelected) {
              zIndex = '100';
          }
          element.style.zIndex = zIndex;
          // --- End zIndex update ---
      }
    });
    
    // Remove markers that are no longer needed
    existingMarkerIds.forEach(id => {
      if (!newMarkerIds.has(id)) {
        const marker = markers.current[id];
        if (marker) {
          marker.remove();
          delete markers.current[id];
        }
      }
    });
    
    // Update connections using GeoJSON
    if (nodeConnections.length > 0) {
      // Create GeoJSON features for connections
      const features: Feature<LineString>[] = nodeConnections
        .filter(connection => {
          // Filter out connections involving nodes that don't exist
          const sourceNode = nodePositions.find(node => node.nodeId === connection.sourceId);
          const targetNode = nodePositions.find(node => node.nodeId === connection.targetId);
          
          if (!sourceNode || !targetNode) return false;
          
          // Skip connections where either endpoint has invalid coordinates
          if (!isFinite(sourceNode.longitude) || !isFinite(sourceNode.latitude) ||
              !isFinite(targetNode.longitude) || !isFinite(targetNode.latitude) ||
              sourceNode.longitude === null || sourceNode.latitude === null ||
              targetNode.longitude === null || targetNode.latitude === null ||
              (sourceNode.latitude === 0 && sourceNode.longitude === 0) ||
              (targetNode.latitude === 0 && targetNode.longitude === 0)) {
            return false;
          }
          
          return true;
        })
        .map(connection => {
          const sourceNode = nodePositions.find(node => node.nodeId === connection.sourceId)!;
          const targetNode = nodePositions.find(node => node.nodeId === connection.targetId)!;
          
          // Determine connection type for the legend
          let connectionType = 'direct_good';
          if (connection.connectionType === 'multi_hop') {
            connectionType = 'multi_hop';
          } else if (connection.connectionType === 'inferred') {
            connectionType = 'inferred';
          } else if (connection.isHistorical) {
            connectionType = 'historical';
          } else if (connection.quality < 40) {
            connectionType = 'direct_poor';
          } else if (connection.quality < 70) {
            connectionType = 'direct_medium';
          }
          
          // Add to set of connection types for the legend
          connectionTypeSet.add(connectionType);
          
          return {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [
                [sourceNode.longitude, sourceNode.latitude],
                [targetNode.longitude, targetNode.latitude]
              ]
            },
            properties: {
              quality: connection.quality,
              sourceId: connection.sourceId,
              targetId: connection.targetId,
              connectionType: connection.connectionType || 'direct',
              hopCount: connection.hopCount || 1,
              isHistorical: connection.isHistorical || false
            }
          } as Feature<LineString>;
        });
      
      // Create the GeoJSON structure
      const geoJson: FeatureCollection<LineString> = {
        type: 'FeatureCollection',
        features
      };
      
      // Update source data
      if (map.current.getSource('connections')) {
        (map.current.getSource('connections') as mapboxgl.GeoJSONSource).setData(geoJson);
      }
      
      // Update active connection types for legend
      setActiveConnectionTypes(Array.from(connectionTypeSet));
    }
    
    // Auto-zoom to fit bounds if explicitly requested via autoCenter prop
    if (visibleNodes.length > 0 && !selectedNodeId && autoCenter) {
      const bounds = new mapboxgl.LngLatBounds();
      visibleNodes.forEach(node => {
        bounds.extend([node.longitude, node.latitude]);
      });
      map.current.fitBounds(bounds, { padding: 50, duration: 1000 });
    }
  }, [mapLoaded, nodePositions, nodeConnections, selectedNodeId, showOfflineNodes, highlightedNodeId, lastHeardNodes, onNodeSelect, autoCenter]);

  // Handle new packet: forward the packet to MapView so that it can highlight the node purple.
  const handleNewPacket = (packet: Packet) => {
    setLatestPacket(packet);
    
    if (isScrollingRef.current) {
      packetQueue.current.push(packet);
      return;
    }
    
    // Clear existing timeouts
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    if (packetPopupTimeoutRef.current) {
      clearTimeout(packetPopupTimeoutRef.current);
    }
    
    const nodeId = typeof packet.fromNode === 'string' 
      ? parseInt(packet.fromNode, 10) 
      : packet.fromNode;
    
    const nodeIdStr = String(nodeId);
    
    // Update state (triggers re-render which applies styles via useEffect)
    setHighlightedNodeId(nodeId);
    
    // Update lastHeardNodes to keep track of the 5 most recent nodes
    setLastHeardNodes(prev => {
      // Remove the nodeId if it's already in the array
      const filtered = prev.filter(id => id !== nodeId);
      // Add the nodeId to the beginning of the array (most recent)
      const newNodes = [nodeId, ...filtered].slice(0, 5); // Keep only the 5 most recent
      
      // Check if any nodes were removed from the top 5, and remove their popups
      const removedNodes = filtered.slice(4); // Nodes beyond index 4 are now out of the top 5
      removedNodes.forEach(removedNodeId => {
        const removedNodeStr = String(removedNodeId);
        if (packetPopupsRef.current[removedNodeStr]) {
          packetPopupsRef.current[removedNodeStr].remove();
          delete packetPopupsRef.current[removedNodeStr];
        }
      });
      
      return newNodes;
    });
    
    // --- Add Persistent Popup Logic & Set Highlighted Marker Z-Index --- 
    const marker = markers.current[nodeIdStr];
    if (marker && map.current && nodePositions) { // Ensure nodePositions is available
      
      // --- Immediately set z-index for highlighted marker ---
      try {
          const markerEl = marker.getElement();
          if (markerEl) {
              markerEl.style.zIndex = '2000'; // Ensure highlighted marker is on top
          }
      } catch (e) {
          console.warn("Could not set z-index on highlighted marker element:", e);
      }
      // --- End immediate z-index setting ---

      const sourceNode = nodePositions.find(n => n.nodeId === nodeId);
      const sourceName = (sourceNode?.details?.shortName || `Node ${nodeId}`).trim(); 
      
      let popupText = sourceName; // Default to source name for broadcast
      const isBroadcast = packet.toNode === undefined || packet.toNode === null || packet.toNode === 255 || packet.toNode === 0 || packet.toNode === 0xFF;
      
      if (!isBroadcast) {
        const targetNodeId = typeof packet.toNode === 'string' ? parseInt(packet.toNode, 10) : packet.toNode;
        const targetNode = nodePositions.find(n => n.nodeId === targetNodeId);
        const targetName = (targetNode?.details?.shortName || `Node ${targetNodeId}`).trim();
        popupText = `${sourceName} -> ${targetName}`; // Format for directed packets
      }
      
      // --- Updated Popup Content & Styling --- 
      const popupContent = `
        <div class="packet-popup">
          ${popupText}
        </div>
      `;
      
      // Remove any existing popup for this node
      if (packetPopupsRef.current[nodeIdStr]) {
        packetPopupsRef.current[nodeIdStr].remove();
      }
      
      // Create and show the popup
      packetPopupsRef.current[nodeIdStr] = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: [0, -40], 
        anchor: 'bottom',
        maxWidth: 'none',
        className: 'packet-info-popup'
      })
        .setLngLat(marker.getLngLat())
        .setHTML(popupContent)
        .addTo(map.current);
        
      // Set z-index on the popup element
      try {
        const popupElement = packetPopupsRef.current[nodeIdStr].getElement();
        if (popupElement) {
            popupElement.style.zIndex = '2500'; // Ensure popup is above markers
        }
      } catch (e) {
        console.warn("Could not set z-index on popup element:", e);
      }
    }
    // --- End Popup Logic & Z-Index Update --- 
    
    // Cleanup for lastHeardNodes
    if (lastHeardCleanupRef.current) {
      clearTimeout(lastHeardCleanupRef.current);
    }
    lastHeardCleanupRef.current = setTimeout(() => {
      // When we remove a node from lastHeardNodes, also remove its popup
      setLastHeardNodes(prev => {
        const newNodes = prev.filter(id => id !== nodeId);
        // Remove the popup if the node is no longer in the list
        if (newNodes.indexOf(nodeId) === -1) {
          if (packetPopupsRef.current[nodeIdStr]) {
            packetPopupsRef.current[nodeIdStr].remove();
            delete packetPopupsRef.current[nodeIdStr];
          }
        }
        return newNodes;
      });
    }, 45000); // 45 seconds

    // Make sure the cleanup timeout correctly resets standard marker transforms
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedNodeId(null); 

      const markerToReset = markers.current[nodeIdStr];
      if (markerToReset && nodePositions) { // Also check nodePositions here
          const markerEl = markerToReset.getElement();
          if (markerEl) {
              const node = nodePositions.find(n => n.nodeId === nodeId);
              // Check if it's an emoji marker - should not be styled directly
              const isEmoji = node?.details?.shortName && containsEmoji(node.details.shortName);
              
              // --- Corrected z-index reset logic ---
              const isSelected = nodeId === selectedNodeId;
              let resetZIndex = '50'; // Default
              if (isSelected) {
                  resetZIndex = '100';
              } else if (node?.isGateway) {
                  resetZIndex = '50'; // Gateways get default
              } else if (node?.isOnline === false) {
                  resetZIndex = '25'; // Offline non-selected/non-gateway have lowest
              }
              markerEl.style.zIndex = resetZIndex;
              // --- End corrected z-index reset logic ---

              if (!isEmoji) { // Only reset standard markers' color and scale
                  const svgPath = markerEl.querySelector('path');
                  if (svgPath) {
                      // Re-apply color based on current state (online/offline/gateway)
                      let finalColor = '#95a5a6'; // Default offline
                      if (node?.isGateway) finalColor = '#3498db';
                      else if (node?.isOnline) finalColor = '#2ecc71';
                      svgPath.setAttribute('fill', finalColor);
                  }
                  
                  // Reset scale based on selection state AFTER highlight ends
                  if (isSelected) {
                      markerEl.style.transform = 'translate(-50%, -50%) scale(1.1)';
                  } else {
                      markerEl.style.transform = 'translate(-50%, -50%) scale(1.0)'; 
                  }
              } else {
                   // Reset emoji marker size based on selection state
                   const finalSize = isSelected ? 32 : 28;
                   markerEl.style.fontSize = `${finalSize}px`;
              }
          }
      }
    }, 5000); // Highlight duration
  };
  
  // Queue packet for highlighting
  const queuePacketForHighlight = (packet: Packet) => {
    packetQueue.current.push(packet);
  };
  
  // Auto-center function
  const autoCenterMap = useCallback((force: boolean = false) => {
    if (!map.current || !nodePositions || nodePositions.length === 0) return;
    
    // Only perform auto-center if forced or autoCenter prop is true
    if (!force && !autoCenter) return;
    
    const filteredNodes = nodePositions.filter(node => {
      return (showOfflineNodes || node.isOnline) &&
             node.latitude != null && node.longitude != null &&
             isFinite(node.latitude) && isFinite(node.longitude);
    });
    
    if (filteredNodes.length === 0) return;
    
    const bounds = new mapboxgl.LngLatBounds();
    filteredNodes.forEach(node => {
      bounds.extend([node.longitude, node.latitude]);
    });
    
    map.current.fitBounds(bounds, { padding: 50, duration: 1000 });
  }, [map, nodePositions, showOfflineNodes, autoCenter]);
  
  // Track initial centering of the map
  const initialCenterRef = useRef(false);
  
  // Automatically center the map once when first loaded with data
  useEffect(() => {
    if (!map.current || !nodePositions || nodePositions.length === 0 || !mapLoaded || initialCenterRef.current) return;
    
    // Center the map once - this ignores the autoCenter prop for the initial view
    console.log('Performing initial map centering');
    autoCenterMap(true);
    initialCenterRef.current = true;
  }, [mapLoaded, nodePositions, autoCenterMap]);
  
  // Expose functions via ref
  useImperativeHandle(ref, () => ({
    autoCenter: autoCenterMap,
    handleNewPacket,
    get isScrolling() {
      return isScrollingRef.current;
    },
    queuePacketForHighlight,
    activeConnectionTypes
  }));
  
  // Loading state
  if (isLoadingPositions || isLoadingConnections || !mapLoaded) {
    return (
      <MapContainer>
        <div ref={mapContainer} className="map-container" />
        <LoadingOverlay>
          <Spinner size={40} />
          <LoadingText>Loading map data...</LoadingText>
        </LoadingOverlay>
      </MapContainer>
    );
  }
  
  // Error state
  if (positionsError || connectionsError) {
    return (
      <MapContainer>
        <div ref={mapContainer} className="map-container" />
        <ErrorOverlay>
          <ErrorMessage message="Error loading map data. Please try again later." />
          <RetryButton onClick={() => {
            refetchPositions();
            refetchConnections();
          }}>
            Retry
          </RetryButton>
        </ErrorOverlay>
      </MapContainer>
    );
  }
  
  // Normal render with data
  return (
    <MapContainer>
      <div ref={mapContainer} className="map-container" />
      
      {/* Debug panel */}
      {showDebug && nodePositions && (
        <DebugPanel data-testid="debug-panel">
          {latestPacket && (() => { // Use an IIFE to calculate packet info
            const sourceNodeId = typeof latestPacket.fromNode === 'string' 
                ? parseInt(latestPacket.fromNode, 10) 
                : latestPacket.fromNode;
            const sourceNode = nodePositions.find(n => n.nodeId === sourceNodeId);
            const sourceName = (sourceNode?.details?.shortName || `Node ${sourceNodeId}`).trim();
            return (
              <h4 style={{ marginBottom: '15px', paddingBottom: '8px', borderBottom: '1px solid #444' }}> 
                  Latest Packet: {sourceName}
              </h4>
            );
          })()}
          {!latestPacket && <h4>Modern Map Debug Info</h4>} {/* Fallback header */}
          
          <p>Map Initialized: <strong>{mapInitialized ? 'Yes' : 'No'}</strong></p>
          <p>Map Loaded: <strong>{mapLoaded ? 'Yes' : 'No'}</strong></p>
          <p>Node Positions: <strong>{nodePositions ? nodePositions.length : 0}</strong></p>
          <p>Node Connections: <strong>{nodeConnections ? nodeConnections.length : 0}</strong></p>
          <p>Selected Node: <strong>{selectedNodeId || 'None'}</strong></p>
          <p>Show Offline Nodes: <strong>{showOfflineNodes ? 'Yes' : 'No'}</strong></p>
          <p>Is Scrolling: <strong>{isScrollingRef.current ? 'Yes' : 'No'}</strong></p>
          
          <button onClick={() => autoCenterMap(true)}>
            Center Map
          </button>
          <button onClick={() => onShowDebugChange?.(false)}>
            Hide Debug Info
          </button>
        </DebugPanel>
      )}
    </MapContainer>
  );
});

// Styled components
const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  flex: 1;
  min-height: 500px;
  background-color: #f0f0f0;
  
  .map-container {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
  
  /* Style for popup to ensure it's above markers */
  .mapboxgl-popup-main {
    z-index: 3000 !important;
  }
  
  /* Ensure close button is visible and properly sized */
  .mapboxgl-popup-main .mapboxgl-popup-close-button {
    font-size: 18px;
    padding: 3px
    color: #333;
    z-index: 3001;
  }
  
  /* Make sure popup content is nicely formatted */
  .mapboxgl-popup-content {
    padding: 6px;
    white-space: nowrap;
  }
  
  /* Fix for node name whitespace in popups */
  .mapboxgl-popup-content strong {
    white-space: pre;
  }
  
  /* Node popup styling */
  .popup-content {
    font-family: sans-serif;
  }
  
  .node-name {
    margin-bottom: 4px;
    white-space: nowrap;
  }
  
  .node-status {
    margin-bottom: 4px;
  }
  
  .node-battery {
    margin-bottom: 4px;
  }
  
  .node-coords {
    font-size: 11px;
  }
  
  /* Packet popup styling */
  .packet-popup {
    font-size: 12px; 
    font-family: sans-serif; 
    font-weight: bold; 
    padding: 2px; 
    color: #333; 
    text-shadow: 0 0 2px #fff, 0 0 3px #fff, 0 0 5px #fff; 
    pointer-events: none; 
    white-space: nowrap;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 10;
`;

const LoadingText = styled.div`
  margin-top: 10px;
  font-size: 16px;
  font-weight: 500;
`;

const ErrorOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 10;
  padding: 20px;
  text-align: center;
`;

const DebugPanel = styled.div`
  position: absolute;
  top: 160px; /* Adjusted top position */
  left: 20px;
  background-color: rgba(45, 52, 54, 0.9);
  color: white;
  padding: 15px;
  border-radius: 8px;
  z-index: 3000;
  font-size: 12px;
  max-width: 250px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
  border: 2px solid #3498db;
  
  h4 {
    margin: 0 0 10px 0; /* Reset margin */
    color: #3498db;
    font-size: 14px;
    /* Removed border-bottom here, added conditionally in JSX */
    padding-bottom: 0; 
  }
  
  p {
    margin: 5px 0;
    font-size: 11px;
    
    strong {
      color: #2ecc71;
      font-weight: bold;
    }
  }
  
  button {
    margin-top: 10px;
    padding: 7px 12px;
    font-size: 12px;
    display: block;
    width: 100%;
    margin-bottom: 5px;
    cursor: pointer;
    background-color: #3498db;
    color: white;
    border: none;
    border-radius: 4px;
    font-weight: bold;
    
    &:hover {
      background-color: #2980b9;
      transform: translateY(-1px);
      transition: all 0.2s;
    }
    
    &:active {
      transform: translateY(0);
    }
  }
`;

const ControlsPanel = styled.div<{$collapsed?: boolean}>`
  background-color: rgba(255, 255, 255, 0.9);
  padding: ${props => props.$collapsed ? '4px 8px' : '8px'};
  border-radius: 4px;
  font-size: 12px;
  width: 100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.1);
  height: ${props => props.$collapsed ? '32px' : 'auto'};
  /* When collapsed, ensure the height is fixed */
  ${props => props.$collapsed && `
    min-height: 32px;
    max-height: 32px;
  `}
`;

const ControlsHeader = styled.div<StyledHeaderProps>`
  margin: 0;
  padding-bottom: ${props => !props.$collapsed ? '4px' : '0'};
  border-bottom: ${props => !props.$collapsed ? '1px solid #e0e0e0' : 'none'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;

const HeaderText = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary || '#3498db'};
`;

const CollapseArrow = styled.span`
  font-size: 12px;
  color: ${props => props.theme.colors.primary || '#3498db'};
  margin-left: 8px;
`;

const LegendSection = styled.div`
  margin-bottom: 6px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const LegendTitle = styled.h5`
  margin: 0 0 4px 0;
  font-size: 11px;
  font-weight: 600;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const NodeDot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  margin-right: 6px;
`;

const LegendText = styled.span`
  font-size: 10px;
`;

const ConnectionLine = styled.div`
  width: 100%;
  height: 2px;
  background-color: #3498db;
  margin: 3px 0;
`;

const DebugToggle = styled.div`
  margin-top: 10px;
  text-align: right;
`;

const DebugButton = styled.button`
  padding: 6px 10px;
  background-color: rgba(52, 152, 219, 0.8);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background-color: rgba(52, 152, 219, 1);
  }
`;

const RetryButton = styled.button`
  margin-top: 20px;
  padding: 8px 16px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: #2980b9;
  }
`;

const EmojiNode = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  font-size: 14px;
`;

const ColorScale = styled.div`
  display: flex;
  height: 16px; /* Reduced height */
  width: 140px;
  border-radius: 4px;
  overflow: visible;
  margin-right: 6px;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.1);
  margin-bottom: 16px; /* Reduced bottom margin */
`;

const ColorStep = styled.div<{ color: string }>`
  flex: 1;
  background-color: ${props => props.color};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:first-child {
    border-top-left-radius: 3px;
    border-bottom-left-radius: 3px;
  }
  
  &:last-child {
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
  }
`;

const ColorLabel = styled.span`
  font-size: 8px; /* Smaller font */
  color: #333;
  background: rgba(255, 255, 255, 0.9);
  padding: 1px 2px;
  border-radius: 2px;
  position: absolute;
  bottom: -14px; /* Adjusted position */
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
`;

const LegendContent = styled.div`
  padding-top: 4px;
  max-height: 300px;
  overflow-y: auto;
`;

const BasicLegendItems = styled.div`
  display: flex;
  flex-direction: column;
  
  @media (min-height: 600px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2px;
  }
`;

const ConnectionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  
  @media (min-height: 700px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2px;
  }
`;

const RecentlyHeardScale = styled.div`
  display: flex;
  height: 16px; /* Reduced height */
  width: 140px;
  border-radius: 4px;
  overflow: visible;
  margin-right: 6px;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.1);
  margin-bottom: 16px; /* Reduced bottom margin */
`;

const RecentlyHeardStep = styled.div<{ color: string }>`
  flex: 1;
  background-color: ${props => props.color};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:first-child {
    border-top-left-radius: 3px;
    border-bottom-left-radius: 3px;
  }
  
  &:last-child {
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
  }
`;

const RecentlyHeardLabel = styled.span`
  font-size: 8px; /* Smaller font */
  color: #333;
  background: rgba(255, 255, 255, 0.9);
  padding: 1px 2px;
  border-radius: 2px;
  position: absolute;
  bottom: -14px; /* Adjusted position */
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
`;

export default ModernView; 